import { useEffect, useRef, useState } from "react";
import { LocateFixed, MapPin } from "lucide-react";
import { toast } from "sonner";
import { t } from "@/lib/i18n";

const PHOTON = "https://photon.komoot.io";
const IATA = {
  FCO: "Aeroporto di Roma-Fiumicino", CIA: "Aeroporto di Roma-Ciampino", MXP: "Aeroporto di Milano-Malpensa", LIN: "Aeroporto di Milano-Linate",
  BGY: "Aeroporto di Bergamo-Orio al Serio", PSA: "Aeroporto di Pisa-San Giusto", FLR: "Aeroporto di Firenze-Peretola", VCE: "Aeroporto di Venezia-Tessera",
  NAP: "Aeroporto di Napoli-Capodichino", CTA: "Aeroporto di Catania-Fontanarossa", PMO: "Aeroporto di Palermo", BLQ: "Aeroporto di Bologna",
  BRI: "Aeroporto di Bari", CAG: "Aeroporto di Cagliari-Elmas", TRN: "Aeroporto di Torino-Caselle", GOA: "Aeroporto di Genova", VRN: "Aeroporto di Verona-Villafranca",
  OLB: "Aeroporto di Olbia", CDG: "Aéroport Paris-Charles-de-Gaulle", ORY: "Aéroport de Paris-Orly", NCE: "Aéroport Nice Côte d'Azur",
  LHR: "Heathrow Airport", LGW: "Gatwick Airport", MUC: "Flughafen München", FRA: "Flughafen Frankfurt am Main", ZRH: "Flughafen Zürich",
  GVA: "Aéroport de Genève", VIE: "Flughafen Wien-Schwechat", BCN: "Aeropuerto de Barcelona-El Prat", MAD: "Aeropuerto Adolfo Suárez Madrid-Barajas",
  AMS: "Amsterdam Airport Schiphol",
};
const photonLang = () => "default";
const label = (p) => {
  const main = p.name || [p.street, p.housenumber].filter(Boolean).join(" ");
  const place = [p.city, p.county, p.state].find((x) => x && x !== main);
  return [main, place, p.countrycode && p.countrycode !== "IT" ? p.country : null].filter(Boolean).join(", ");
};
const toItem = (f) => ({ label: label(f.properties), city: f.properties.city || f.properties.name, lat: f.geometry.coordinates[1], lon: f.geometry.coordinates[0] });

const search = async (q, signal) => {
  const code = IATA[q.toUpperCase()];
  const url = (text, n) => `${PHOTON}/api/?q=${encodeURIComponent(text)}&limit=${n}&lang=${photonLang()}&lat=44&lon=10&zoom=4`;
  const queries = code ? [[code, 1], [q, 4]] : [[q, 7]];
  const lists = await Promise.all(queries.map(([text, n]) => fetch(url(text, n), { signal }).then((r) => r.json())));
  const items = lists.flatMap((d, i) => d.features.map((f) => (code && i === 0 ? { ...toItem(f), label: `${q.toUpperCase()} • ${code}` } : toItem(f))));
  const seen = new Set();
  return items.filter((it) => it.label && !seen.has(it.label) && seen.add(it.label)).slice(0, 7);
};

const reverse = async (lat, lon) => {
  const r = await fetch(`${PHOTON}/reverse?lat=${lat}&lon=${lon}&lang=${photonLang()}`);
  const f = (await r.json()).features?.[0];
  return { label: f ? label(f.properties) : `${lat.toFixed(5)}, ${lon.toFixed(5)}`, city: f?.properties?.city || f?.properties?.name, lat, lon };
};

export const AddressInput = ({ value, onChange, onPick, placeholder, testid, gps, gpsInline, className = "", inputClassName = "", autoFocus, onEnter }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [locating, setLocating] = useState(false);
  const typed = useRef(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!typed.current || value.trim().length < 2) {
      setItems([]);
      return undefined;
    }
    const ctrl = new AbortController();
    const timer = setTimeout(() => search(value.trim(), ctrl.signal).then((list) => { setItems(list); setOpen(document.activeElement === inputRef.current); }).catch(() => {}), 200);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [value]);

  const pick = (it) => {
    typed.current = false;
    onChange(it.label);
    onPick?.(it);
    setOpen(false);
  };

  const locate = () => {
    if (!navigator.geolocation) return toast.error(t("Posizione non disponibile"));
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => { pick(await reverse(coords.latitude, coords.longitude).catch(() => ({ label: "", lat: coords.latitude, lon: coords.longitude }))); setLocating(false); },
      () => { toast.error(t("Posizione non disponibile")); setLocating(false); },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  return (
    <div className={`relative ${className}`}>
      <input ref={inputRef} autoFocus={autoFocus} data-testid={testid} className={`ff-field ${gpsInline ? "!pr-14" : ""} ${inputClassName}`} placeholder={placeholder} value={value} autoComplete="off"
        onChange={(e) => { typed.current = true; onChange(e.target.value); }}
        onFocus={() => items.length && setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => { if (e.key === "Enter" && onEnter) { setOpen(false); onEnter(); } }} />
      {gpsInline && (
        <button type="button" data-testid={`${testid}-gps`} onClick={locate} disabled={locating} aria-label={t("Posizione Attuale")} title={t("Posizione Attuale")}
          className="absolute right-2 top-1/2 -translate-y-1/2 h-11 w-11 rounded-lg flex items-center justify-center text-[#10B981] hover:bg-[#18181B] transition-colors">
          <LocateFixed size={20} className={locating ? "animate-spin" : ""} />
        </button>
      )}
      {gps && (
        <button type="button" data-testid={`${testid}-gps`} onClick={locate} disabled={locating} className="mt-2 flex items-center gap-2 text-sm font-semibold text-[#6EE7B7] hover:text-white">
          <LocateFixed size={16} className={locating ? "animate-spin" : ""} /> {t("Usa la mia posizione")}
        </button>
      )}
      {open && items.length > 0 && (
        <ul data-testid={`${testid}-suggestions`} className="absolute z-[70] left-0 right-0 top-[calc(100%+4px)] rounded-xl border border-[#27272A] bg-[#0A0A0A] shadow-2xl overflow-hidden">
          {items.map((it, i) => (
            <li key={it.label}>
              <button type="button" data-testid={`${testid}-suggestion-${i}`} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(it)} className="w-full flex items-center gap-2 px-4 py-3 text-left text-sm font-semibold text-white hover:bg-[#18181B]">
                <MapPin size={15} className="text-[#10B981] shrink-0" /> <span className="truncate">{it.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
